// CHomeScene — 首頁（原劇院構圖與街機按鈕）
// 對應 PRD §4.3、§9.1 解析度與縮放、§10 Accessibility、§11.3 設定
//
// === 使用圖片 ===
// - public/assets/ui/home-title.png（key: HOME_TITLE_KEY）— 原遊戲 logo
// - public/assets/ui/home-cards.png（key: HOME_CARDS_KEY）— 八張麻將牌扇
// - public/assets/ui/home-btn-generic-frame.png（key: HOME_BUTTON_GENERIC_FRAME_KEY）— 後備焦點框
// - public/assets/ui/cream-mascot.png（key: CREAM_MASCOT_KEY）— 開始按鈕小牌主角
// - 主按鈕：CArcadeButtonSurface；設定按鈕與彈窗：dialog-skin；設定選項：CSettingsControl，詳見各檔頭。
// - public/assets/ui/home-main-bg.png（key: HOME_MAIN_BG_KEY）— 紫色背景
// - public/assets/ui/home-curtain-top.png / home-curtain-side.png（HOME_CURTAIN_TOP_KEY / HOME_CURTAIN_SIDE_KEY）— 紅幕
// - public/assets/ui/home-npc.png / home-npc-ears.png（HOME_NPC_KEY / HOME_NPC_EARS_KEY）— 原角色及兔耳
// - public/assets/ui/focus-star.png（HOME_FOCUS_STAR_KEY）— 原星環動畫
// 右上金幣、每日簽到、救濟金：CHomeAccountBar（src/ui/home-account-bar.ts，詳見該檔頭）。
// 左上分級標示與條款連結：createHomeLegalBadge（純 Graphics，詳見該檔頭）。
// 設定右上 X：close-button（純 Graphics，詳見該檔頭）。
// 舊齒輪、木紋按鈕及開關素材預留但目前未採用。

import { showDialog, hideDialog, reducedMotion, watchReducedMotion, Motion } from '@ui/motion';
import Phaser from 'phaser';
import { createCloseButton, CloseButtonLayout } from '@ui/close-button';
import { BGMTrack } from '@audio/audio-keys';
import { playUIClick, syncBackgroundMusic } from '@audio/scene-audio';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '@config/game-config';
import { GameSetStatus, prepareNextRound, rollDice } from '@core/index';
import {
  CSpatialKeyboardNavigator,
  KeyboardNavigationResult,
  type IKeyboardFocusTarget,
} from '@input/spatial-keyboard-navigator';
import { traceGameSet } from '../debug/round-trace';
import { ZH_TW } from '@i18n/zh-tw';
import {
  abandonActiveSession,
  loadGameSave,
  loadGameSaveResult,
  loadSettings,
  saveGameSave,
  saveActiveSession,
  updateSettings,
} from '@storage/index';
import { createDialogPanel, createDialogButtonSurface } from '@ui/dialog-skin';
import { CArcadeButtonSurface } from '@ui/arcade-button-surface';
import { CSettingsControl, SettingsControlKind } from '@ui/settings-control';
import { attachPressFeedback } from '@ui/index';
import { createPageButton, PAGE_THEME } from '@ui/page-skin';

import {
  HOME_BUTTON_GENERIC_FRAME_KEY,
  CREAM_MASCOT_KEY,
  CREAM_ASSET_FRAME,
  HOME_MAIN_BG_KEY,
  HOME_CURTAIN_TOP_KEY,
  HOME_CURTAIN_SIDE_KEY,
  HOME_TITLE_KEY,
  HOME_CARDS_KEY,
  HOME_NPC_KEY,
  HOME_NPC_EARS_KEY,
  HOME_FOCUS_STAR_KEY,
} from './boot-scene';
import {
  abandonSet,
  claimRelief,
  lineLoginEnabled,
  nextRound,
  onAccountChange,
  refreshAccount,
  signIn,
  startLineLogin,
} from '@net/account-client';
import { isServerTableEnabled } from '@net/server-mode';
import { showConnectionError } from '@ui/connection-error';
import { CHomeAccountBar } from '@ui/home-account-bar';
import { createHomeLegalBadge } from '@ui/home-legal-badge';
import { maybeShowInstallHint } from '@ui/install-hint';
import { RELIEF_THRESHOLD } from '@net/account-rules';
import { maybeShowHelpPrompt } from '@ui/help-prompt';
import { noticeFromError, showNotice } from '@ui/notice-toast';

const ACCOUNT_BAR_DEPTH = 50; // 金幣條在角色與簾幕之上、對話框（100）之下
import { SceneKey } from './scene-keys';

// ============================================================================
// 版面常數（所有大小、顏色、座標微調都改這裡）
// ============================================================================

// ---- 紅幕（劇院簾幕）----
const CURTAIN_TOP_SCALE = 1.46; // 上方簾頭縮放（882→約鋪滿 1280 寬）
const CURTAIN_SIDE_SCALE = 1.0; // 側邊簾幕縮放
const CURTAIN_SIDE_TOP_Y = 0; // 側邊簾幕 origin 上緣 Y

// ---- 背景紫色底（main_bg：108×504 直條，橫向重複鋪出垂直光紋）----
const BG_TILE_SCALE = 1.43; // tileSprite 縮放（504→約 720 填滿高度；橫向 repeat 出多道垂直光紋）

// ---- 「至尊麻將王」logo ----
const TITLE_Y = 150; // logo 中心 Y
const TITLE_SCALE = 0.82; // logo 縮放（900px → 約 740）

// ---- 兔女郎角色 + 兔耳 ----
const NPC_X = 640; // 角色中心 X（畫面正中央）
const NPC_Y = 430; // 角色中心 Y
const NPC_SCALE = 1.12; // 角色縮放
const EARS_X = 640; // 兔耳中心 X（對齊頭頂）
const EARS_Y = 278; // 兔耳中心 Y
const EARS_SCALE = 0.92; // 兔耳縮放

// ---- 兔女郎頭部星星圓環擴散（home-focus-star：3×4=12 frames，每格 54×54）----
// 一圈星星均勻排在圓周上，整圈從頭部中心由小放大向外展開、同時自轉並淡出，無限重複。
// 多層環錯開時間 → 連續的「光環一圈圈往外擴散」效果。畫在兔女郎之後方。
const STAR_RING_X = 640; // 圓環中心 X（兔女郎頭部）
const STAR_RING_Y = 295; // 圓環中心 Y（兔女郎頭部）
const STAR_FRAMES: readonly number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]; // 12 frames 隨機挑選
const STAR_RING_COUNT = 10; // 一圈幾顆星星
const STAR_TILE_SCALE = 0.85; // 單顆星星縮放
const STAR_RING_BASE_RADIUS = 110; // 星星在環上的基準半徑（會隨整環 scale 一起擴張）
const STAR_RING_SCALE_FROM = 0.1; // 環起始縮放（小環，聚在中心）
const STAR_RING_SCALE_TO = 1.5; // 環結束縮放（大環，擴散到外圈）
const STAR_RING_DURATION_MS = 1300; // 一次展開時長
const STAR_RING_ROTATE_DEG = 24; // 展開過程整環自轉角度
const STAR_RING_WAVES = 3; // 同時幾層環（彼此錯開週期 → 連續一圈圈擴散）

// ---- 麻將牌裝飾：8 張牌「像扇子」散在角色身後（共同軸心 + 各牌展開角度）----
// 做法：每張牌掛在一個位於 FAN_PIVOT 的軸心 container 上、往上偏移 FAN_RADIUS；
//   container 旋轉到該牌的 angle 即定位（收合 = 全部 angle 0 疊在一起朝上，展開 = 各自轉到 angle）。
// home-cards spritesheet：4×2 grid（frame 0–7）。
const CARDS_SCALE = 0.62; // 單張牌縮放
const FAN_PIVOT_X = 640; // 扇子軸心 X（畫面中央，藏在角色身後）
const FAN_PIVOT_Y = 900; // 扇子軸心 Y（移到畫面下方外，使牌落在大圓頂端 → 弧線平、偏水平展開）
const FAN_RADIUS = 610; // 牌中心距軸心的半徑（軸心遠 + 半徑大 = 扇面平而寬）
// 每筆：{ frame, angle }（angle 度，順時針為正＝往右展開；負＝往左）
// 軸心移遠 + 角度收小 → 弧線變平：牌往左右展開、垂直落差小
const HOME_CARD_FAN: ReadonlyArray<{ readonly frame: number; readonly angle: number }> = [
  // 左側（內→外）：往左展開
  { frame: 0, angle: -12 },
  { frame: 1, angle: -22 },
  { frame: 2, angle: -32 },
  { frame: 3, angle: -42 },
  // 右側（內→外）：往右展開
  { frame: 4, angle: 12 },
  { frame: 5, angle: 22 },
  { frame: 6, angle: 32 },
  { frame: 7, angle: 42 },
];

const RESUME_DIALOG_X = LOGICAL_WIDTH / 2;
const RESUME_DIALOG_Y = LOGICAL_HEIGHT / 2;
const RESUME_DIALOG_WIDTH = 620;
const RESUME_DIALOG_HEIGHT = 270;
const RESUME_DIALOG_BUTTON_Y = 442; // 按鈕與面板下緣保留呼吸空間
const RESUME_DIALOG_BUTTON_WIDTH = 210;
const RESUME_DIALOG_BUTTON_HEIGHT = 58;
const RESUME_DIALOG_CONTINUE_X = 515;
const RESUME_DIALOG_ABANDON_X = 765;
const SAVE_ERROR_TITLE_Y_OFFSET = 48;
const SAVE_ERROR_MESSAGE_Y_OFFSET = 25;
const SAVE_ERROR_TITLE_FONT_SIZE = 34;
const SAVE_ERROR_MESSAGE_FONT_SIZE = 22;

// ---- 「開始遊戲」主按鈕（紅色立體底 + 字）----
const START_BTN_X = 640; // 主按鈕中心 X
const START_BTN_Y = 648; // 主按鈕中心 Y
const START_BTN_FRAME_W = 342; // 主按鈕寬
const START_BTN_FRAME_H = 82; // 主按鈕高
const START_BTN_FONT_SIZE = 34; // 主按鈕米白文字大小
const START_MASCOT_HEIGHT = 88; // 主按鈕左側小牌高度
const START_MASCOT_LEFT = 34; // 小牌中心距按鈕左邊
const START_LABEL_OFFSET = 24; // 主文字避開小牌
const START_BTN_TEXT_COLOR = '#fff0d8'; // 霧面上的米白字
const START_BTN_HIT_PAD = 24; // 點擊熱區比視覺按鈕大的額外邊距
const KEYBOARD_FOCUS_DEPTH = 200; // 焦點框高於首頁與設定面板

// ---- 左下設定按鈕 ----
const BOTTOM_BTN_Y = 660; // 設定鈕中心 Y
const SETTING_BTN_X = 58; // 設定鈕中心 X
const SETTING_BTN_WIDTH = 90; // 設定鈕寬
const SETTING_BTN_HEIGHT = 52; // 設定鈕高
const SHOP_BTN_X = 1222; // 商店鈕中心 X（右下，與設定鈕對稱）
const SHOP_BTN_WIDTH = 90; // 商店鈕寬
const RECORDS_BTN_X = 1122; // 紀錄鈕中心 X（商店鈕左側）

// ---- 設定面板 ----
const PANEL_WIDTH = 480; // 面板寬
const PANEL_HEIGHT = 480; // 面板高
const BACKDROP_ALPHA = 0.55; // 半透明背景遮罩透明度（0=透明、1=不透明）
const BACKDROP_COLOR = 0x000000; // 背景遮罩底色

// 面板內元素相對 panel 中心 (0,0) 的座標
const PANEL_TITLE_OFFSET_FROM_TOP = 42; // 面板標題距面板頂部的距離
const PANEL_TITLE_FONT_SIZE = '32px'; // 面板標題字體

const TOGGLE_ROW_AUDIO_Y = -110; // 音效切換列 Y（面板中心相對）
const TOGGLE_ROW_BGM_Y = -50; // BGM 切換列 Y
const TOGGLE_ROW_DOUBLE_TAP_Y = 10; // 點兩下才出牌切換列 Y
const TIMEOUT_LABEL_Y = 78; // 出牌秒數 label Y
const TIMEOUT_BUTTON_Y = 128; // 出牌秒數按鈕列 Y
const TIMEOUT_BUTTON_STEP_X = 105; // 出牌秒數按鈕之間水平間距
const TIMEOUT_BUTTON_WIDTH = 103; // 相鄰分段保留 2px 間隔
const TIMEOUT_BUTTON_HEIGHT = 42; // 秒數選項高度
const SWITCH_WIDTH = 104; // 滑動開關寬度
const SWITCH_HEIGHT = 40; // 滑動開關高度
const TIMEOUT_AREA_PAD_LEFT = 30; // 出牌秒數區起始 X 相對面板左邊的偏移

const SETTINGS_LABEL_FONT_SIZE = '24px'; // 設定 label 字體

// Toggle row 內元素位置
const TOGGLE_ROW_LABEL_X = -200; // Toggle row label 距面板中心的 X 偏移（負值=左側）
const TOGGLE_ROW_BUTTON_X = 80; // Toggle row button 距面板中心的 X 偏移
const TOGGLE_ROW_FONT_SIZE = '24px'; // Toggle row 字體

// ---- 出牌秒數選項清單（label 一律取 i18n，不在此重複定義字串避免漂移） ----
const TIMEOUT_OPTIONS: ReadonlyArray<{ label: string; value: 8 | 15 | 30 | null }> = [
  { label: ZH_TW.settings.discardTimeoutOptions[8], value: 8 },
  { label: ZH_TW.settings.discardTimeoutOptions[15], value: 15 },
  { label: ZH_TW.settings.discardTimeoutOptions[30], value: 30 },
  { label: ZH_TW.settings.discardTimeoutOptions.none, value: null },
];

// ============================================================================
// 進場 / 待機動畫參數（適中強度）
// ============================================================================
const ANIM_CURTAIN_MS = 460; // 紅幕滑入時長
const ANIM_LOGO_MS = 520; // logo 落下時長
const ANIM_LOGO_DROP_FROM = 240; // logo 起始 Y（往上偏移量）
const ANIM_CHAR_MS = 460; // 兔女郎 pop 時長
const ANIM_CARD_MS = 460; // 牌從中央滑開到扇位的時長
const ANIM_BTN_IN_MS = 360; // 開始鈕淡入時長
const ANIM_DELAY_LOGO = 240; // logo 進場延遲
const ANIM_DELAY_CHAR = 520; // 兔女郎進場延遲
const ANIM_DELAY_CARDS = 1000; // 牌扇展開延遲（等角色 pop 完、蓋住中央後才從身後滑開）
const ANIM_DELAY_BTN = 1560; // 開始鈕進場延遲（接在牌展開完成之後）
// ---- 待機（持續循環）----
const IDLE_BREATH_AMP = 3; // 兔女郎呼吸上下幅度（px）
const IDLE_BREATH_MS = 1900; // 兔女郎呼吸半週期
const IDLE_LOGO_AMP = 0.008; // logo 呼吸縮放幅度（±比例）
const IDLE_LOGO_MS = 2400; // logo 呼吸半週期
const EARS_FRAME_RATE = 3; // 兔耳擺動 frameRate（fps）
const EARS_ANIM_KEY = 'home-ears-wiggle';

// ============================================================================
// CHomeScene
// ============================================================================

export class CHomeScene extends Phaser.Scene {
  private startButtonSurface?: CArcadeButtonSurface;
  private homeDecorations: Phaser.GameObjects.GameObject[] = [];
  private resumeDialog: Phaser.GameObjects.Container | null = null;
  private accountBar: CHomeAccountBar | null = null;
  private accountReady: Promise<void> = Promise.resolve();
  /** 設定面板的 Container（用於 toggle 顯示/隱藏） */
  private settingsPanel: Phaser.GameObjects.Container | null = null;

  private readonly keyboardNavigator = new CSpatialKeyboardNavigator();
  private keyboardFocusFrame!: Phaser.GameObjects.Image;
  private homeKeyboardTargets: IKeyboardFocusTarget[] = [];
  private panelKeyboardTargets: IKeyboardFocusTarget[] = [];
  private resumeKeyboardTargets: IKeyboardFocusTarget[] = [];

  constructor() {
    super({ key: SceneKey.Home });
  }

  create(): void {
    syncBackgroundMusic(this, BGMTrack.Lobby);
    const cx = LOGICAL_WIDTH / 2;

    this.homeDecorations = [];
    this.cameras.main.resetFX();
    this.settingsPanel = null;
    this.resumeDialog = null;
    // ============ 背景：紫色漸層（橫向重複鋪出垂直光紋，仿原圖 main_bg）============
    if (this.textures.exists(HOME_MAIN_BG_KEY)) {
      this.add
        .tileSprite(cx, LOGICAL_HEIGHT / 2, LOGICAL_WIDTH, LOGICAL_HEIGHT, HOME_MAIN_BG_KEY)
        .setTileScale(BG_TILE_SCALE, BG_TILE_SCALE);
    }

    // ============ 紅幕（簾頭先畫在後、側幕後畫蓋在上層）============
    // 前後順序：上方簾頭（valance）在底層，左右側幕（垂墜）疊在簾頭之上，與原圖一致。
    let sideL: Phaser.GameObjects.Image | undefined;
    let sideR: Phaser.GameObjects.Image | undefined;
    let topCurtain: Phaser.GameObjects.Image | undefined;
    if (this.textures.exists(HOME_CURTAIN_TOP_KEY)) {
      topCurtain = this.add
        .image(cx, 0, HOME_CURTAIN_TOP_KEY)
        .setOrigin(0.5, 0)
        .setScale(CURTAIN_TOP_SCALE);
    }
    if (this.textures.exists(HOME_CURTAIN_SIDE_KEY)) {
      sideL = this.add
        .image(0, CURTAIN_SIDE_TOP_Y, HOME_CURTAIN_SIDE_KEY)
        .setOrigin(0, 0)
        .setScale(CURTAIN_SIDE_SCALE);
      sideR = this.add
        .image(LOGICAL_WIDTH, CURTAIN_SIDE_TOP_Y, HOME_CURTAIN_SIDE_KEY)
        .setOrigin(1, 0)
        .setFlipX(true)
        .setScale(CURTAIN_SIDE_SCALE);
    }

    // ============ 麻將牌扇形裝飾（軸心 container，先畫讓角色蓋在上層）============
    // 每張牌掛在 FAN_PIVOT 的軸心上、往上偏移 FAN_RADIUS；container 旋轉到 angle 即定位。
    const cardPivots: Phaser.GameObjects.Container[] = [];
    if (this.textures.exists(HOME_CARDS_KEY)) {
      for (const c of HOME_CARD_FAN) {
        const pivot = this.add.container(FAN_PIVOT_X, FAN_PIVOT_Y);
        pivot.add(this.add.image(0, -FAN_RADIUS, HOME_CARDS_KEY, c.frame).setScale(CARDS_SCALE));
        pivot.setAngle(c.angle); // 靜態最終態 = 展開
        cardPivots.push(pivot);
      }
    }

    // ============ 兔女郎頭部星星擴散（畫在兔女郎之前 → 位於其後方）============
    this.createStarBurst();

    // ============ 兔女郎 + 兔耳（包成 container 以利整體 pop / 呼吸）============
    // container 置於角色中心，子物件以相對座標掛入；兔耳掛在頭頂偏移處
    let charContainer: Phaser.GameObjects.Container | undefined;
    if (this.textures.exists(HOME_NPC_KEY)) {
      charContainer = this.add.container(NPC_X, NPC_Y);
      charContainer.add(this.add.image(0, 0, HOME_NPC_KEY).setScale(NPC_SCALE));
      if (this.textures.exists(HOME_NPC_EARS_KEY)) {
        this.ensureEarsAnim();
        const ears = this.add
          .sprite(EARS_X - NPC_X, EARS_Y - NPC_Y, HOME_NPC_EARS_KEY, 0)
          .setScale(EARS_SCALE);
        this.homeDecorations.push(ears);
        if (!reducedMotion()) ears.play(EARS_ANIM_KEY); // 兔耳擺動（持續）
        charContainer.add(ears);
      }
    }

    // ============ 「至尊麻將王」logo ============
    let title: Phaser.GameObjects.Image | undefined;
    if (this.textures.exists(HOME_TITLE_KEY)) {
      title = this.add.image(cx, TITLE_Y, HOME_TITLE_KEY).setScale(TITLE_SCALE);
    }

    // ============ 開始遊戲主按鈕 ============
    // 開始 → 載入續局或底台選擇，再經擲骰進牌局
    const startBtn = this.createStartButton(START_BTN_X, START_BTN_Y, () => this.handleStartGame());

    const settingBtn = createPageButton(
      this,
      SETTING_BTN_X,
      BOTTOM_BTN_Y,
      SETTING_BTN_WIDTH,
      SETTING_BTN_HEIGHT,
      '設定'
    );
    settingBtn.setInteractive({ useHandCursor: true });
    settingBtn.bindInteraction(settingBtn);
    attachPressFeedback(settingBtn, settingBtn, {
      pressScale: Motion.pressScale,
      onClick: () => this.toggleSettingsPanel(),
    });

    // 商店（伺服器模式才有：金幣與付款都在伺服器）
    if (isServerTableEnabled()) {
      const shopBtn = createPageButton(
        this,
        SHOP_BTN_X,
        BOTTOM_BTN_Y,
        SHOP_BTN_WIDTH,
        SETTING_BTN_HEIGHT,
        '商店'
      );
      shopBtn.setInteractive({ useHandCursor: true });
      shopBtn.bindInteraction(shopBtn);
      attachPressFeedback(shopBtn, shopBtn, {
        pressScale: Motion.pressScale,
        onClick: () => this.scene.start(SceneKey.Shop),
      });
      const recordsBtn = createPageButton(
        this,
        RECORDS_BTN_X,
        BOTTOM_BTN_Y,
        SETTING_BTN_WIDTH,
        SETTING_BTN_HEIGHT,
        '紀錄'
      );
      recordsBtn.setInteractive({ useHandCursor: true });
      recordsBtn.bindInteraction(recordsBtn);
      attachPressFeedback(recordsBtn, recordsBtn, {
        pressScale: Motion.pressScale,
        onClick: () => this.scene.start(SceneKey.Records),
      });
    }

    this.keyboardFocusFrame = this.add
      .image(0, 0, HOME_BUTTON_GENERIC_FRAME_KEY)
      .setDepth(KEYBOARD_FOCUS_DEPTH)
      .setVisible(false);
    this.homeKeyboardTargets = [
      this.createKeyboardTarget(
        START_BTN_X,
        START_BTN_Y,
        START_BTN_FRAME_W,
        START_BTN_FRAME_H,
        () => {
          playUIClick(this);
          this.handleStartGame();
        },
        HOME_BUTTON_GENERIC_FRAME_KEY,
        this.startButtonSurface
      ),
      ...(settingBtn
        ? [
            this.createKeyboardTarget(
              SETTING_BTN_X,
              BOTTOM_BTN_Y,
              SETTING_BTN_WIDTH,
              SETTING_BTN_HEIGHT,
              () => {
                playUIClick(this);
                this.toggleSettingsPanel();
              },
              HOME_BUTTON_GENERIC_FRAME_KEY,
              settingBtn
            ),
          ]
        : []),
    ];
    this.keyboardNavigator.setTargets(this.homeKeyboardTargets);
    this.bindKeyboard();
    this.setupAccount();
    createHomeLegalBadge(this, ACCOUNT_BAR_DEPTH);

    this.setupHomeAnimations({
      topCurtain,
      sideL,
      sideR,
      cardPivots,
      charContainer,
      title,
      startBtn,
    });
  }

  /** 伺服器模式：同步帳號（第一次開啟會建立訪客帳號並發新手禮），右上角顯示金幣與簽到。 */
  private setupAccount(): void {
    this.accountReady = Promise.resolve();
    this.accountBar = null;
    if (!isServerTableEnabled()) return;
    const bar = new CHomeAccountBar(
      this,
      {
        onSignIn: () => this.handleSignIn(),
        onRelief: () => this.handleRelief(),
        onLineLogin: () => {
          playUIClick(this);
          startLineLogin().catch((error: unknown) => noticeFromError(error));
        },
      },
      ACCOUNT_BAR_DEPTH
    );
    this.accountBar = bar;
    bar.showOffline();
    const off = onAccountChange((snapshot) => bar.update(snapshot));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, off);
    this.accountReady = refreshAccount()
      .then((snapshot) => {
        bar.update(snapshot);
        // 換手機也找得回帳號：伺服器開放 LINE 登入、這個帳號還沒綁定時顯示按鈕
        if (!snapshot.lineLinked)
          void lineLoginEnabled().then((enabled) => {
            if (this.scene.isActive()) bar.setLineLoginVisible(enabled);
          });
        if (snapshot.recentLedger.length === 1 && snapshot.recentLedger[0]!.kind === 'newbie-gift')
          showNotice(`歡迎！送你新手禮 ${snapshot.coins.toLocaleString()} 金幣`);
        else if (snapshot.coins < RELIEF_THRESHOLD && snapshot.reliefLeft === 0)
          maybeShowHelpPrompt('金幣輸光了', () => this.scene.start(SceneKey.Shop));
        else maybeShowInstallHint();
      })
      .catch((error: unknown) => {
        console.error('[account]', error);
        showConnectionError(error instanceof Error ? error.message : String(error));
      });
  }

  private handleSignIn(): void {
    playUIClick(this);
    signIn()
      .then((reward) => showNotice(`簽到成功！獲得 ${reward.toLocaleString()} 金幣`))
      .catch((error: unknown) => noticeFromError(error));
  }

  private handleRelief(): void {
    playUIClick(this);
    claimRelief()
      .then((reward) => showNotice(`領到救濟金 ${reward.toLocaleString()} 金幣`))
      .catch((error: unknown) => noticeFromError(error));
  }

  private handleStartGame(): void {
    // 伺服器模式要等帳號同步完，存檔快取才是最新的
    void this.accountReady.then(() => this.handleStartGameNow());
  }

  private handleStartGameNow(): void {
    try {
      const loaded = loadGameSaveResult();
      const save = loaded.save;
      if (loaded.corruptSessionDiscarded) {
        saveGameSave(save);
        traceGameSet(
          'session.abandoned',
          null,
          { reason: 'corrupt-active-session' },
          save.ledger.balances
        );
      }
      const active = save.activeSession;
      if (!active) {
        this.scene.start(SceneKey.TableSelect);
        return;
      }
      if (active.status === GameSetStatus.Completed) {
        this.scene.start(SceneKey.SessionSummary);
        return;
      }
      this.showResumeDialog();
    } catch (error) {
      this.showFatalSaveError(error);
    }
  }

  private resumeActiveGame(): void {
    this.closeResumeDialog();
    if (isServerTableEnabled()) {
      // 伺服器先把中途離開的那一手代打完並結算，再準備下一手
      nextRound()
        .then((autoFinished) => {
          if (autoFinished) {
            const delta = autoFinished.deltaBySeat[0];
            showNotice(
              `上一手已由 AI 代打完成\n${delta >= 0 ? '贏' : '輸'} ${Math.abs(delta).toLocaleString()} 金幣`
            );
          }
          const status = loadGameSave().activeSession?.status;
          if (!status) this.scene.start(SceneKey.TableSelect);
          else if (status === GameSetStatus.Completed) this.scene.start(SceneKey.SessionSummary);
          else if (status === GameSetStatus.CircleComplete) this.scene.start(SceneKey.GameSetup);
          else this.scene.start(SceneKey.Dice, { autoStartGame: true });
        })
        .catch((error: unknown) => noticeFromError(error));
      return;
    }
    const save = loadGameSave();
    const active = save.activeSession;
    if (!active) {
      this.scene.start(SceneKey.TableSelect);
      return;
    }
    traceGameSet('session.resumed', active, {}, save.ledger.balances);
    if (active.status === GameSetStatus.Completed) {
      this.scene.start(SceneKey.SessionSummary);
      return;
    }
    if (active.status === GameSetStatus.CircleComplete) {
      this.scene.start(SceneKey.GameSetup);
      return;
    }
    if (active.status === GameSetStatus.BetweenRounds) {
      const id = (prefix: string): string =>
        `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1_000_000_000)}`;
      const next = prepareNextRound(active, {
        roundId: id('round'),
        transactionId: id('settlement'),
        openingDice: rollDice(Math.random),
        wallSeed: Math.floor(Math.random() * 0x1_0000_0000),
      });
      saveActiveSession(next);
      traceGameSet('round.plan.saved', next, {}, save.ledger.balances);
    }
    this.scene.start(SceneKey.Dice, { autoStartGame: true });
  }

  private showResumeDialog(): void {
    if (this.resumeDialog) return;
    this.resumeKeyboardTargets = [];
    const container = this.add.container(0, 0).setDepth(100);
    const shade = this.add
      .rectangle(
        LOGICAL_WIDTH / 2,
        LOGICAL_HEIGHT / 2,
        LOGICAL_WIDTH,
        LOGICAL_HEIGHT,
        0x000000,
        0.72
      )
      .setInteractive();
    const panel = createDialogPanel(
      this,
      RESUME_DIALOG_X,
      RESUME_DIALOG_Y,
      RESUME_DIALOG_WIDTH,
      RESUME_DIALOG_HEIGHT
    );
    const title = this.add
      .text(RESUME_DIALOG_X, RESUME_DIALOG_Y - 65, '發現尚未完成的牌局', {
        color: '#f4dba1',
        fontFamily: PAGE_THEME.font,
        fontSize: '34px',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const message = this.add
      .text(RESUME_DIALOG_X, RESUME_DIALOG_Y - 15, '要繼續原牌局，還是放棄進度？', {
        color: PAGE_THEME.text,
        fontFamily: PAGE_THEME.font,
        fontSize: '24px',
      })
      .setOrigin(0.5);
    const continueButton = this.createResumeDialogButton(RESUME_DIALOG_CONTINUE_X, '繼續牌局', () =>
      this.resumeActiveGame()
    );
    const abandonButton = this.createResumeDialogButton(RESUME_DIALOG_ABANDON_X, '放棄牌局', () => {
      if (isServerTableEnabled()) {
        this.closeResumeDialog();
        abandonSet()
          .then(() => this.scene.start(SceneKey.TableSelect))
          .catch((error: unknown) => noticeFromError(error));
        return;
      }
      const save = loadGameSave();
      traceGameSet('session.abandoned', save.activeSession, {}, save.ledger.balances);
      abandonActiveSession();
      this.closeResumeDialog();
      this.scene.start(SceneKey.TableSelect);
    });
    container.add([shade, panel, title, message, ...continueButton, ...abandonButton]);
    showDialog(this, container, shade, RESUME_DIALOG_X, RESUME_DIALOG_Y);
    this.resumeDialog = container;
    this.keyboardNavigator.setTargets(this.resumeKeyboardTargets);
  }

  private closeResumeDialog(): void {
    if (this.resumeDialog) hideDialog(this, this.resumeDialog, true);
    this.resumeDialog = null;
  }

  private createResumeDialogButton(
    x: number,
    label: string,
    onActivate: () => void
  ): Phaser.GameObjects.GameObject[] {
    const graphics = createDialogButtonSurface(
      this,
      x,
      RESUME_DIALOG_BUTTON_Y,
      RESUME_DIALOG_BUTTON_WIDTH,
      RESUME_DIALOG_BUTTON_HEIGHT
    );
    graphics.setPrimary(x === RESUME_DIALOG_CONTINUE_X);
    const text = this.add
      .text(x, RESUME_DIALOG_BUTTON_Y, label, {
        color: PAGE_THEME.text,
        fontFamily: PAGE_THEME.font,
        fontSize: '26px',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const zone = this.add
      .zone(x, RESUME_DIALOG_BUTTON_Y, RESUME_DIALOG_BUTTON_WIDTH, RESUME_DIALOG_BUTTON_HEIGHT)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        playUIClick(this);
        onActivate();
      });
    graphics.bindInteraction(zone);
    this.resumeKeyboardTargets.push(
      this.createKeyboardTarget(
        x,
        RESUME_DIALOG_BUTTON_Y,
        RESUME_DIALOG_BUTTON_WIDTH,
        RESUME_DIALOG_BUTTON_HEIGHT,
        () => {
          playUIClick(this);
          onActivate();
        },
        HOME_BUTTON_GENERIC_FRAME_KEY,
        graphics
      )
    );
    return [graphics, text, zone];
  }

  /** 永久籌碼 root 損壞時停止建立牌局；正式 UI 不提供靜默重設帳本。 */
  private showFatalSaveError(error: unknown): void {
    if (this.resumeDialog) return;
    const container = this.add.container(0, 0).setDepth(100);
    this.keyboardNavigator.clear();
    const shade = this.add
      .rectangle(
        LOGICAL_WIDTH / 2,
        LOGICAL_HEIGHT / 2,
        LOGICAL_WIDTH,
        LOGICAL_HEIGHT,
        0x000000,
        0.78
      )
      .setInteractive();
    const panel = createDialogPanel(
      this,
      RESUME_DIALOG_X,
      RESUME_DIALOG_Y,
      RESUME_DIALOG_WIDTH,
      RESUME_DIALOG_HEIGHT
    );
    const title = this.add
      .text(RESUME_DIALOG_X, RESUME_DIALOG_Y - SAVE_ERROR_TITLE_Y_OFFSET, '永久籌碼資料錯誤', {
        color: '#f4dba1',
        fontFamily: PAGE_THEME.font,
        fontSize: `${SAVE_ERROR_TITLE_FONT_SIZE}px`,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const detail = error instanceof Error ? error.message : String(error);
    const message = this.add
      .text(
        RESUME_DIALOG_X,
        RESUME_DIALOG_Y + SAVE_ERROR_MESSAGE_Y_OFFSET,
        `已停止建立新牌局，永久籌碼不會被重設。\n${detail}`,
        {
          align: 'center',
          color: PAGE_THEME.text,
          fontFamily: PAGE_THEME.font,
          fontSize: `${SAVE_ERROR_MESSAGE_FONT_SIZE}px`,
          wordWrap: { width: RESUME_DIALOG_WIDTH - 70 },
        }
      )
      .setOrigin(0.5);
    container.add([shade, panel, title, message]);
    showDialog(this, container, shade, RESUME_DIALOG_X, RESUME_DIALOG_Y);
    this.resumeDialog = container;
  }

  private createStarBurst(): void {
    if (reducedMotion() || !this.textures.exists(HOME_FOCUS_STAR_KEY)) return;
    for (let w = 0; w < STAR_RING_WAVES; w++) {
      const previous = new Set(this.children.list);
      this.spawnStarRing((STAR_RING_DURATION_MS / STAR_RING_WAVES) * w);
      this.homeDecorations.push(...this.children.list.filter((child) => !previous.has(child)));
    }
  }

  /** 建立一層星星環，並掛上「由中心向外展開→淡出→重複」的循環 tween。 */
  private spawnStarRing(delay: number): void {
    const ring = this.add.container(STAR_RING_X, STAR_RING_Y);
    const stars: Phaser.GameObjects.Image[] = [];
    const pickFrame = (): number => STAR_FRAMES[Math.floor(Math.random() * STAR_FRAMES.length)]!;
    for (let i = 0; i < STAR_RING_COUNT; i++) {
      const ang = (i / STAR_RING_COUNT) * Math.PI * 2;
      const sx = Math.cos(ang) * STAR_RING_BASE_RADIUS;
      const sy = Math.sin(ang) * STAR_RING_BASE_RADIUS;
      const star = this.add
        .image(sx, sy, HOME_FOCUS_STAR_KEY, pickFrame())
        .setScale(STAR_TILE_SCALE);
      stars.push(star);
      ring.add(star);
    }

    ring.setScale(STAR_RING_SCALE_FROM).setAlpha(1);
    this.tweens.add({
      targets: ring,
      scale: { value: STAR_RING_SCALE_TO, ease: 'Cubic.easeOut' }, // 快速往外擴張
      angle: { value: STAR_RING_ROTATE_DEG, ease: 'Sine.easeOut' },
      alpha: { value: 0, ease: 'Quart.easeIn' }, // 擴散過程維持明亮、接近外圈才淡出
      duration: STAR_RING_DURATION_MS,
      delay,
      repeat: -1,
      onRepeat: () => {
        // 每一圈重新隨機挑 frame，讓每波星星不同
        for (const s of stars) s.setFrame(pickFrame());
      },
    });
  }

  /** 建立兔耳擺動 animation（只建一次）。 */
  private ensureEarsAnim(): void {
    if (this.anims.exists(EARS_ANIM_KEY)) return;
    this.anims.create({
      key: EARS_ANIM_KEY,
      frames: this.anims.generateFrameNumbers(HOME_NPC_EARS_KEY, { frames: [0, 1] }),
      frameRate: EARS_FRAME_RATE,
      repeat: -1,
    });
  }

  /**
   * 進場動畫（一次性，依序播放）+ 待機循環（進場後接續）。
   * 各元素：把終值記住 → 設成進場起始狀態 → tween 回終值 → onComplete 起待機循環。
   */
  private setupHomeAnimations(refs: {
    readonly topCurtain?: Phaser.GameObjects.Image | undefined;
    readonly sideL?: Phaser.GameObjects.Image | undefined;
    readonly sideR?: Phaser.GameObjects.Image | undefined;
    readonly cardPivots: Phaser.GameObjects.Container[];
    readonly charContainer?: Phaser.GameObjects.Container | undefined;
    readonly title?: Phaser.GameObjects.Image | undefined;
    readonly startBtn?: Phaser.GameObjects.Container | undefined;
  }): void {
    const { topCurtain, sideL, sideR, cardPivots, charContainer, title, startBtn } = refs;
    const settle = (): void => {
      for (const target of [
        topCurtain,
        sideL,
        sideR,
        ...cardPivots,
        charContainer,
        title,
        startBtn,
        ...this.homeDecorations,
      ]) {
        if (target) this.tweens.killTweensOf(target);
      }
      sideL?.setX(0);
      sideR?.setX(LOGICAL_WIDTH);
      topCurtain?.setY(0);
      title?.setY(TITLE_Y).setScale(TITLE_SCALE).setAlpha(1);
      charContainer?.setY(NPC_Y).setScale(1).setAlpha(1);
      cardPivots.forEach((pivot, i) => pivot.setAngle(HOME_CARD_FAN[i]!.angle).setAlpha(1));
      startBtn?.setScale(1).setAlpha(1);
      for (const decoration of this.homeDecorations) {
        if (decoration instanceof Phaser.GameObjects.Sprite) decoration.stop();
        else if (decoration instanceof Phaser.GameObjects.Container) decoration.setAlpha(0);
      }
    };
    watchReducedMotion(this, () => {
      if (reducedMotion()) settle();
    });
    const returning = this.registry.get('motion-home-visited') === true;
    this.registry.set('motion-home-visited', true);
    if (reducedMotion()) {
      settle();
      return;
    }
    if (returning) {
      this.cameras.main.fadeIn(Motion.returnMs);
      if (title) this.startLogoIdle(title);
      if (charContainer) this.startCharBreath(charContainer);
      return;
    }

    // --- 紅幕：兩側從畫面外滑入、簾頭由上落下 ---
    if (sideL) {
      const w = sideL.displayWidth;
      sideL.x = -w;
      this.tweens.add({ targets: sideL, x: 0, duration: ANIM_CURTAIN_MS, ease: 'Quad.easeOut' });
    }
    if (sideR) {
      const w = sideR.displayWidth;
      sideR.x = LOGICAL_WIDTH + w;
      this.tweens.add({
        targets: sideR,
        x: LOGICAL_WIDTH,
        duration: ANIM_CURTAIN_MS,
        ease: 'Quad.easeOut',
      });
    }
    if (topCurtain) {
      const h = topCurtain.displayHeight;
      topCurtain.y = -h;
      this.tweens.add({
        targets: topCurtain,
        y: 0,
        duration: ANIM_CURTAIN_MS,
        ease: 'Quad.easeOut',
      });
    }

    // --- logo：從上方落下、回彈 → 待機呼吸 ---
    if (title) {
      title.setAlpha(0).setY(TITLE_Y - ANIM_LOGO_DROP_FROM);
      this.tweens.add({
        targets: title,
        y: TITLE_Y,
        alpha: 1,
        delay: ANIM_DELAY_LOGO,
        duration: ANIM_LOGO_MS,
        ease: 'Back.easeOut',
        onComplete: () => this.startLogoIdle(title),
      });
    }

    // --- 兔女郎：由小 pop 彈入 → 待機呼吸 ---
    if (charContainer) {
      charContainer.setScale(0).setAlpha(0);
      this.tweens.add({
        targets: charContainer,
        scale: 1,
        alpha: 1,
        delay: ANIM_DELAY_CHAR,
        duration: ANIM_CHAR_MS,
        ease: 'Back.easeOut',
        onComplete: () => this.startCharBreath(charContainer),
      });
    }

    // --- 牌扇：像扇子一樣繞共同軸心展開 ---
    // 收合 = 全部 angle 0（疊在一起朝上、藏在角色身後）；展開 = 各 container 轉到 final angle。
    // 左 4 轉負（往左）、右 4 轉正（往右），同時；onStart 即時現身（非淡入）。
    cardPivots.forEach((pivot) => {
      const fa = pivot.angle; // 建立時已設成展開角度
      pivot.setAngle(0).setAlpha(0); // 收合 + 隱形
      this.tweens.add({
        targets: pivot,
        angle: fa,
        delay: ANIM_DELAY_CARDS,
        duration: ANIM_CARD_MS,
        ease: 'Back.easeOut',
        onStart: () => pivot.setAlpha(1),
      });
    });

    // --- 開始鈕：淡入 + 輕彈 → 待機脈動 ---
    if (startBtn) {
      startBtn.setAlpha(0).setScale(0.6);
      this.tweens.add({
        targets: startBtn,
        alpha: 1,
        scale: 1,
        delay: ANIM_DELAY_BTN,
        duration: ANIM_BTN_IN_MS,
        ease: 'Back.easeOut',
      });
    }
  }

  /** 待機：logo 微幅縮放呼吸。 */
  private startLogoIdle(title: Phaser.GameObjects.Image): void {
    this.tweens.add({
      targets: title,
      scale: TITLE_SCALE * (1 + IDLE_LOGO_AMP),
      duration: IDLE_LOGO_MS,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /** 待機：兔女郎上下浮動呼吸。 */
  private startCharBreath(charContainer: Phaser.GameObjects.Container): void {
    this.tweens.add({
      targets: charContainer,
      y: NPC_Y - IDLE_BREATH_AMP,
      duration: IDLE_BREATH_MS,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /** 霧面主按鈕，文字、小牌與表面分開渲染。 */
  private createStartButton(
    x: number,
    y: number,
    onClick: () => void
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const surface = new CArcadeButtonSurface(this, START_BTN_FRAME_W, START_BTN_FRAME_H);
    surface.setPrimary();
    this.startButtonSurface = surface;
    const label = this.add
      .text(START_LABEL_OFFSET, 0, ZH_TW.home.start, {
        fontFamily: PAGE_THEME.font,
        fontSize: `${START_BTN_FONT_SIZE}px`,
        fontStyle: 'bold',
        color: START_BTN_TEXT_COLOR,
      })
      .setOrigin(0.5);
    const mascotFrame = this.textures.getFrame(CREAM_MASCOT_KEY, CREAM_ASSET_FRAME);
    const mascot = this.add
      .image(-START_BTN_FRAME_W / 2 + START_MASCOT_LEFT, 0, CREAM_MASCOT_KEY, CREAM_ASSET_FRAME)
      .setScale(START_MASCOT_HEIGHT / mascotFrame.height);
    container.add([surface, mascot, label]);
    container.setSize(START_BTN_FRAME_W, START_BTN_FRAME_H);
    const hit = this.add
      .zone(x, y, START_BTN_FRAME_W + START_BTN_HIT_PAD, START_BTN_FRAME_H + START_BTN_HIT_PAD)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerover', () => surface.setAppearance(false, true, false));
    hit.on('pointerout', () => surface.setAppearance(false, false, false));
    hit.on('pointerdown', () => surface.setAppearance(false, true, true));
    hit.on('pointerup', () => surface.setAppearance(false, true, false));
    hit.on('pointerupoutside', () => surface.setAppearance(false, false, false));
    attachPressFeedback(hit, container, {
      pressScale: Motion.pressScale,
      onClick,
      onPressStart: () => {
        this.tweens.killTweensOf(container);
        container.setAlpha(1);
      },
    });
    surface.setAppearance(false, false, false);
    return container;
  }

  // ----------------------------------------------------------
  // 設定面板（PRD §11.3）
  // ----------------------------------------------------------

  private toggleSettingsPanel(): void {
    if (this.settingsPanel) {
      hideDialog(this, this.settingsPanel, true);
      this.settingsPanel = null;
      this.panelKeyboardTargets = [];
      this.keyboardNavigator.setTargets(this.homeKeyboardTargets);
      return;
    }
    this.settingsPanel = this.buildSettingsPanel();
    this.keyboardNavigator.setTargets(this.panelKeyboardTargets);
  }

  private buildSettingsPanel(): Phaser.GameObjects.Container {
    const cx = LOGICAL_WIDTH / 2;
    const cy = LOGICAL_HEIGHT / 2;
    const container = this.add.container(cx, cy);
    this.panelKeyboardTargets = [];

    // 半透明全屏遮罩（點外側關閉）
    const backdrop = this.add
      .rectangle(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT, BACKDROP_COLOR, BACKDROP_ALPHA)
      .setOrigin(0.5)
      .setInteractive();
    backdrop.on('pointerup', () => this.toggleSettingsPanel());
    container.add(backdrop);

    // 面板底
    const bg = createDialogPanel(this, 0, 0, PANEL_WIDTH, PANEL_HEIGHT);
    container.add(bg);

    // 面板裡的物件不該被 backdrop 點擊事件擋到 → 設定 interactive 阻擋穿透
    bg.setInteractive();
    bg.on('pointerup', () => {
      // 吃掉事件，避免穿透到 backdrop
    });

    // 標題
    container.add(
      this.add
        .text(0, -PANEL_HEIGHT / 2 + PANEL_TITLE_OFFSET_FROM_TOP, ZH_TW.settings.title, {
          fontFamily: PAGE_THEME.font,
          fontSize: PANEL_TITLE_FONT_SIZE,
          fontStyle: 'bold',
          color: '#fff5d6',
        })
        .setOrigin(0.5)
    );

    // 載入當前設定
    let settings = loadSettings();

    // 1. 音效開關
    this.addToggleRow(
      container,
      TOGGLE_ROW_AUDIO_Y,
      ZH_TW.settings.audio,
      settings.audioOn,
      (v) => {
        settings = updateSettings({ audioOn: v });
      }
    );

    // 2. 背景音樂開關
    this.addToggleRow(container, TOGGLE_ROW_BGM_Y, ZH_TW.settings.bgm, settings.bgmOn, (v) => {
      settings = updateSettings({ bgmOn: v });
      syncBackgroundMusic(this, BGMTrack.Lobby);
    });

    // 3. 點兩下才出牌（防誤觸）
    this.addToggleRow(
      container,
      TOGGLE_ROW_DOUBLE_TAP_Y,
      ZH_TW.settings.doubleTapDiscard,
      settings.doubleTapDiscard,
      (v) => {
        settings = updateSettings({ doubleTapDiscard: v });
      }
    );

    // 4. 出牌秒數
    container.add(
      this.add
        .text(
          -PANEL_WIDTH / 2 + TIMEOUT_AREA_PAD_LEFT,
          TIMEOUT_LABEL_Y,
          ZH_TW.settings.discardTimeout,
          {
            fontFamily: PAGE_THEME.font,
            fontSize: SETTINGS_LABEL_FONT_SIZE,
            color: PAGE_THEME.text,
          }
        )
        .setOrigin(0, 0.5)
    );

    const timerControls: CSettingsControl[] = [];
    TIMEOUT_OPTIONS.forEach((opt, index) => {
      const control = new CSettingsControl(this, {
        x: -PANEL_WIDTH / 2 + TIMEOUT_AREA_PAD_LEFT + TIMEOUT_BUTTON_STEP_X * (index + 0.5),
        y: TIMEOUT_BUTTON_Y,
        width: TIMEOUT_BUTTON_WIDTH,
        height: TIMEOUT_BUTTON_HEIGHT,
        kind: SettingsControlKind.Segment,
        label: opt.label,
        selected: settings.discardTimeout === opt.value,
        onActivate: () => {
          settings = updateSettings({ discardTimeout: opt.value });
          timerControls.forEach((item, itemIndex) => item.setSelected(itemIndex === index));
        },
      });
      timerControls.push(control);
      container.add(control);
      this.panelKeyboardTargets.push(
        this.createKeyboardTarget(
          cx + control.x,
          cy + control.y,
          control.width,
          control.height,
          () => control.activate(),
          HOME_BUTTON_GENERIC_FRAME_KEY,
          control
        )
      );
    });

    const close = createCloseButton(
      this,
      PANEL_WIDTH / 2 - CloseButtonLayout.insetX,
      -PANEL_HEIGHT / 2 + CloseButtonLayout.insetY,
      () => {
        playUIClick(this);
        this.toggleSettingsPanel();
      }
    );
    container.add(close.root);
    this.panelKeyboardTargets.push(
      this.createKeyboardTarget(
        cx + close.x,
        cy + close.y,
        CloseButtonLayout.hitSize,
        CloseButtonLayout.hitSize,
        close.activate,
        HOME_BUTTON_GENERIC_FRAME_KEY,
        close
      )
    );

    // 防止 unused（settings 變數的最後寫入仍有效，這裡只是讓 lint 不抱怨）
    void settings;
    showDialog(this, container, backdrop);
    return container;
  }

  /** 在 settings panel 內建立一個 toggle 欄位（label + 開/關按鈕）。 */
  private addToggleRow(
    container: Phaser.GameObjects.Container,
    y: number,
    label: string,
    initialOn: boolean,
    onChange: (on: boolean) => void
  ): void {
    const labelObj = this.add
      .text(TOGGLE_ROW_LABEL_X, y, label, {
        fontFamily: PAGE_THEME.font,
        fontSize: TOGGLE_ROW_FONT_SIZE,
        color: PAGE_THEME.text,
      })
      .setOrigin(0, 0.5);
    container.add(labelObj);

    let isOn = initialOn;
    const control = new CSettingsControl(this, {
      x: TOGGLE_ROW_BUTTON_X,
      y,
      width: SWITCH_WIDTH,
      height: SWITCH_HEIGHT,
      kind: SettingsControlKind.Switch,
      selected: isOn,
      onActivate: () => {
        isOn = !isOn;
        control.setSelected(isOn);
        onChange(isOn);
      },
    });
    container.add(control);
    this.panelKeyboardTargets.push(
      this.createKeyboardTarget(
        LOGICAL_WIDTH / 2 + control.x,
        LOGICAL_HEIGHT / 2 + control.y,
        control.width,
        control.height,
        () => control.activate(),
        HOME_BUTTON_GENERIC_FRAME_KEY,
        control
      )
    );
  }

  private createKeyboardTarget(
    x: number,
    y: number,
    width: number,
    height: number,
    activate: () => void,
    frameKey: string = HOME_BUTTON_GENERIC_FRAME_KEY,
    surface?: Pick<IKeyboardFocusTarget, 'setFocused'>
  ): IKeyboardFocusTarget {
    return {
      x,
      y,
      activate,
      setFocused: (focused) => {
        if (surface) {
          this.keyboardFocusFrame.setVisible(false);
          surface.setFocused(focused);
          return;
        }
        if (!focused) {
          this.keyboardFocusFrame.setVisible(false);
          return;
        }
        this.keyboardFocusFrame
          .setTexture(frameKey)
          .setPosition(x, y)
          .setDisplaySize(width, height)
          .setVisible(true);
      },
    };
  }

  private bindKeyboard(): void {
    const keyboard = this.input.keyboard;
    if (!keyboard) return;
    const handleKeyboard = (event: KeyboardEvent): void => {
      if ((event.key === 'Escape' || event.key === 'Backspace') && this.settingsPanel !== null) {
        this.toggleSettingsPanel();
        event.preventDefault();
        return;
      }
      if ((event.key === 'Escape' || event.key === 'Backspace') && this.resumeDialog !== null) {
        this.closeResumeDialog();
        this.resumeKeyboardTargets = [];
        this.keyboardNavigator.setTargets(this.homeKeyboardTargets);
        event.preventDefault();
        return;
      }
      if (this.keyboardNavigator.handleKey(event.key) !== KeyboardNavigationResult.Ignored) {
        event.preventDefault();
      }
    };
    keyboard.on('keydown', handleKeyboard);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      keyboard.off('keydown', handleKeyboard);
      this.keyboardNavigator.clear();
    });
  }
}
